import React, { useEffect, useMemo, useState, useCallback, useContext } from 'react';
import { UserContext } from 'contexts/UserContext';
import { Modal as BsModal, Button as BsButton } from 'react-bootstrap';
import { useTravel } from 'v3/pages/Travel';
import Button from 'v3/components/Button';
import { FaCheckCircle, FaExclamationCircle, FaTimesCircle, FaPlus } from 'react-icons/fa';
import { Text } from 'v3/components';
import CreateAdjustment from './Actions/created';
import ConcludeAdjustment from './Actions/conclued';
import AnnulAdjustment from './Actions/anuled';
import * as svc from '../Services';
import * as S from './styles';
import { useSnackbar } from 'v3/components/Snackbar';
import AdjustmentList from './List';

import {
  createAdjustmentSchema,
  concludeAdjustmentSchema,
  annulAdjustmentSchema,
  parseYupErrors,
  parseApiError,
} from '../Services/validations';

const normalizeSlug = (s) => {
  const up = String(s || '').toUpperCase().trim();
  if (!up) return '';
  if (up === 'REGISTRATION') return 'REGISTER';
  if (up === 'NEGOTIATION_CUSTOMER') return 'NEGOTIATOR_CUSTOMER';
  return up;
};

const extractSlugsFromAny = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) {
    return val
      .map((g) => (typeof g === 'string' ? g : g?.slug || g?.code || g?.name))
      .map(normalizeSlug)
      .filter(Boolean);
  }
  const v1 = extractSlugsFromAny(val.groupSlugs);
  const v2 = extractSlugsFromAny(val.groups);
  return [...v1, ...v2];
};

const buildUserGroupSlugs = ({ userGroupsRes, travelCtx, userCtx, lsUser, lsPerms }) => {
  const fromApi     = extractSlugsFromAny(userGroupsRes?.data);
  const fromTravel  = [
    ...extractSlugsFromAny(travelCtx?.auth),
    ...extractSlugsFromAny(travelCtx?.originGroupsRaw),
    ...extractSlugsFromAny(travelCtx?.user),
  ];
  const fromUserCtx = extractSlugsFromAny(userCtx);
  const fromLSUser  = extractSlugsFromAny(lsUser);
  const fromLSPerms = extractSlugsFromAny(lsPerms);

  const all = [...fromApi, ...fromTravel, ...fromUserCtx, ...fromLSUser, ...fromLSPerms]
    .map(normalizeSlug)
    .filter(Boolean);

  return new Set(all);
};

const GROUP_LABELS = {
  NEGOTIATION: 'Negociação',
  OPERATION: 'Operação',
  GRO: 'GRO',
  REGISTRATION: 'Cadastro',
  RISK_MANAGER: 'GR',
  REGISTER: 'Cadastro',
  NEGOTIATOR_CUSTOMER: 'Negociador (Cliente)',
};

const ALLOWED_TO_CREATE = new Set([
  'EMISSION',
  'NEGOTIATION',
  'GRO',
  'REGISTRATION',
  'RISK_MANAGER',
  'REGISTER',
  'NEGOTIATOR_CUSTOMER',
]);

function StatPill({ label, value, color }) {
  return (
    <div style={S.statPill}>
      <div style={{ ...S.statValue, color }}>{value ?? 0}</div>
      <div style={S.statLabel}>{label}</div>
    </div>
  );
}

function StatusChip({ status }) {
  if (status === 'finished') {
    return (
      <span style={{ ...S.chip, ...S.chipOk }}>
        <FaCheckCircle size={12} className="mr-1" />
        <span>Concluída</span>
      </span>
    );
  }
  if (status === 'canceled') {
    return (
      <span style={{ ...S.chip, ...S.chipDanger }}>
        <FaTimesCircle size={12} className="mr-1" />
        <span>Anulada</span>
      </span>
    );
  }
  return (
    <span style={{ ...S.chip, ...S.chipWarn }}>
      <FaExclamationCircle size={12} className="mr-1" />
      <span>Pendente</span>
    </span>
  );
}

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '-');
const fmtDay = (d) => (d ? new Date(d).toLocaleDateString() : '-');
const elapsed = (start, end) => {
  if (!start) return '-';
  const a = new Date(start).getTime();
  const b = end ? new Date(end).getTime() : Date.now();
  const mins = Math.max(0, Math.round((b - a) / 60000));
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h${m ? ` ${m}m` : ''}`;
};

const pickFirst = (...vals) => vals.find(v => v !== undefined && v !== null && String(v).trim() !== '');
const getCurrentUser = (userContext) => {
  const ctxUser = userContext?.user || userContext;
  const ctxId   = ctxUser?.id ?? ctxUser?.userId ?? null;
  const ctxName = ctxUser?.username ?? ctxUser?.login ?? ctxUser?.name ?? null;

  let lsId = null, lsName = null;
  try {
    const ls = JSON.parse(localStorage.getItem('usuario') || 'null');
    lsId   = ls?.id ?? ls?.userId ?? null;
    lsName = ls?.username ?? ls?.login ?? ls?.name ?? null;
  } catch (_) {}

  const id = pickFirst(ctxId, lsId);
  const username = pickFirst(ctxName, lsName);

  return {
    id: Number.isFinite(Number(id)) ? Number(id) : null,
    username: username || '',
  };
};

export default function AdjustmentsModal({ isOpen, onClose, travelId }) {
  const [loading, setLoading] = useState(false);
  const [reasonLoading, setReasonLoading] = useState(false);
  const [mode, setMode] = useState('list');
  const [totals, setTotals] = useState({ total: 0, pending: 0, finished: 0 });
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [groups, setGroups] = useState([]);
  const [originGroups, setOriginGroups] = useState([]);
  const [reasons, setReasons] = useState([]);
  const snackbar = useSnackbar();
  const [form, setForm] = useState({ groupSlug: '', reasonId: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [resolvingId, setResolvingId] = useState(null);
  const [cancelingId, setCancelingId] = useState(null);
  const [actionDesc, setActionDesc] = useState('');

  const [createForm, setCreateForm] = useState({
    fromGroupId: '',
    groupSlug: '',
    reasonId: '',
    description: '',
  });

  const [createErrors, setCreateErrors] = useState({});
  const [createApiError, setCreateApiError] = useState('');
  const [finishErrors, setFinishErrors] = useState({});
  const [finishApiError, setFinishApiError] = useState('');
  const [annulErrors, setAnnulErrors] = useState({});
  const [annulApiError, setAnnulApiError] = useState('');
  const userContext = useContext(UserContext);
  const [userGroupSlugs, setUserGroupSlugs] = useState(new Set());
  const { id: currentUserId, username: currentUsername } = useMemo(
    () => getCurrentUser(userContext),
    [userContext]
  );
  const readLS = () => {
    try {
      const usuario = JSON.parse(localStorage.getItem('usuario') || 'null');
      const permissions = JSON.parse(localStorage.getItem('permissions') || 'null');
      return { usuario, permissions };
    } catch (e) {
      return { usuario: null, permissions: null };
    }
  };

  const travelCtx = useTravel();
  const travelStatus = travelCtx?.data?.travelStatus;
  const statuses = travelCtx?.statuses || [];
  const endLoadingOrder = useMemo(() => {
    const end = statuses.find(
      (s) =>
        String(s.code).toUpperCase() === 'ENDED_LOADING' ||
        String(s.name || '').toLowerCase().includes('carregamento finalizado')
    );
    return Number.isFinite(Number(end?.order))
      ? Number(end.order)
      : Number.POSITIVE_INFINITY;
  }, [statuses]);

  const docIssueStatus = useMemo(() => {
    const status = statuses.find(
      (s) => s.code === 'DOCS_ISSUED'
    )

    return status || null;
  }, [statuses]);

  const canSaveCreate =
    Number.isFinite(Number(createForm.fromGroupId)) &&
    !!createForm.groupSlug &&
    !!createForm.reasonId &&
    String(createForm.description || '').trim().length > 0 &&
    !saving;

  const disabledCreateAdjustment = useMemo(() => {
    if (!originGroups.some(g => ALLOWED_TO_CREATE.has(String(g.slug || '').toUpperCase()))) {
      return 'Você não tem permissão para inserir pendências';
    }

    if (travelStatus.order >= docIssueStatus.order) {
      return 'Não é possível inserir pendências após documentação emitida';
    }

    return '';
  }, [originGroups, docIssueStatus]);

  const handleSelectGroup = (slug) => {
    setCreateForm((f) => ({ ...f, groupSlug: slug, reasonId: '' }));
    setCreateErrors((e) => ({ ...e, groupSlug: undefined, reasonId: undefined }));
    setCreateApiError('');
  };

  const fetchAll = useCallback(async () => {
    if (!travelId) return;
    setLoading(true);
    try {
      const [groupsRes, userGroupsRes, totalsRes, listRes] = await Promise.all([
        svc.getGroups(),
        svc.getUserGroups(),
        svc.getTotals(travelId),
        svc.getTravelAdjustments(travelId),
      ]);

      const { usuario: lsUsuario, permissions: lsPermissions } = readLS();
      const slugs = buildUserGroupSlugs({
        userGroupsRes,
        travelCtx,
        userCtx: userContext,
        lsUser: lsUsuario,
        lsPerms: lsPermissions,
      });
      setUserGroupSlugs(slugs);
      const parsedGroups = (groupsRes?.data || []).map((g) => ({
        slug: g.slug,
        name: GROUP_LABELS[g.slug] || g.name || g.slug,
        id: g.id,
      }));
      setGroups(parsedGroups);

      const parsedOrigin = (userGroupsRes?.data || []).map((g) => ({
        id: g.id,
        slug: g.slug,
        name: GROUP_LABELS[g.slug] || g.name || g.slug,
      }));
      setOriginGroups(parsedOrigin);

      const totalsObj = totalsRes?.data || {};
      const accum = { total: 0, pending: 0, finished: 0 };
      Object.values(totalsObj).forEach((v) => {
        accum.total += Number(v?.total) || 0;
        accum.pending += Number(v?.pending) || 0;
        accum.finished += Number(v?.finished) || 0;
      });
      setTotals(accum);

      const rawList = listRes?.data || [];
      const normalized = rawList.map((it) => {
        const statusSlug = String(it?.status?.slug || '').toUpperCase();
        const isFinished = statusSlug === 'FINISHED';
        const isAnnulled = statusSlug === 'ANNULLED';
        const status = isFinished ? 'finished' : isAnnulled ? 'canceled' : 'pending';
        const lastActor = it.userUpdated?.username || '-';

        return {
          id: it.id,
          groupSlug: it.group?.slug || '',
          groupName: it.group?.name || it.groupSlug || '-',
          fromGroup: it.fromGroup || null,
          reason: it.reason?.description || '-',
          description: it.reasonDescription || '',
          createdById: it.userCreated?.id ?? null,     // <<<<<< ADICIONADO
          createdBy: it.userCreated?.username || '-',
          openedAt: it.createdAt || null,
          finishedAt: isFinished ? (it.finishedAt || null) : null,
          finishedBy: isFinished ? lastActor : '-',
          canceledAt: isAnnulled ? (it.updatedAt || null) : null,
          canceledBy: isAnnulled ? lastActor : '-',
          resolution: (isFinished || isAnnulled) ? (it.finishedDescription || '-') : '-',
          status,
        };
      });

      setItems(normalized);
      setSelected(null);
      setExpandedId(null);
      setMode('list');
      setActionDesc('');
      setForm({ groupSlug: '', reasonId: '', description: '' });
      setCreateForm({ fromGroupId: '', groupSlug: '', reasonId: '', description: '' });
      setCreateErrors({}); setCreateApiError('');
      setFinishErrors({}); setFinishApiError('');
      setAnnulErrors({}); setAnnulApiError('');
    } catch (err) {
      console.error('Erro ao carregar ajustes:', err);
    } finally {
      setResolvingId(null);
      setCancelingId(null);
      setLoading(false);
    }
  }, [travelId, travelCtx, userContext]);

  const handleClose = useCallback(() => {
    setMode('list');
    setSelected(null);
    setExpandedId(null);
    setActionDesc('');
    setForm({ groupSlug: '', reasonId: '', description: '' });
    setCreateForm({ fromGroupId: '', groupSlug: '', reasonId: '', description: '' });
    setCreateErrors({}); setCreateApiError('');
    setFinishErrors({}); setFinishApiError('');
    setAnnulErrors({}); setAnnulApiError('');
    setSaving(false);
    setResolvingId(null);
    setCancelingId(null);
    if (onClose) onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) fetchAll();
  }, [isOpen, fetchAll]);

  useEffect(() => {
    async function loadReasons() {
      if (!createForm.groupSlug) {
        setReasons([]);
        setCreateForm((f) => ({ ...f, reasonId: '' }));
        return;
      }
      try {
        const fromGroupSlug = originGroups.find(
          (g) => String(g.id) === String(createForm.fromGroupId)
        )?.slug;
        setReasonLoading(true);
        const res = await svc.getReasons(fromGroupSlug, createForm.groupSlug);
        const opts = (res?.data || []).map((r) => ({
          id: r.id,
          label: r.description,
        }));
        setReasons(opts);
      } catch (err) {
        console.error('Erro ao buscar motivos:', err);
        setReasons([]);
      } finally {
        setReasonLoading(false);
      }
    }
    loadReasons();
  }, [createForm.groupSlug, createForm.fromGroupId, originGroups]);

  const handleChangeActionDesc = (v) => {
    setActionDesc(v);
    if (mode === 'finish') {
      setFinishErrors({}); setFinishApiError('');
    }
    if (mode === 'cancel') {
      setAnnulErrors({}); setAnnulApiError('');
    }
  };

  const handleCreate = async () => {
    setCreateErrors({});
    setCreateApiError('');

    try {
      await createAdjustmentSchema.validate(
        {
          fromGroupId: createForm.fromGroupId,
          groupSlug: createForm.groupSlug,
          reasonId: createForm.reasonId,
          description: createForm.description,
        },
        { abortEarly: false }
      );
    } catch (yerr) {
      setCreateErrors(parseYupErrors(yerr));
      return;
    }

    const fromGroupIdNum = Number(createForm.fromGroupId);

    const fromGroupSlug =
      originGroups.find(g => g.id === fromGroupIdNum)?.slug ||
      groups.find(g => g.id === fromGroupIdNum)?.slug ||
      null;

    const payload = {
      reasonId: Number(createForm.reasonId),
      description: createForm.description.trim(),
      groupSlug: createForm.groupSlug,
      fromGroupId: fromGroupIdNum,
      ...(fromGroupSlug ? { fromGroup: fromGroupSlug } : {}),
    };

    setSaving(true);
    try {
      await svc.createAdjustment(travelId, payload);
      await fetchAll();
    } catch (err) {
      const { message, fieldErrors } = parseApiError(err, 'Falha ao salvar pendência.');
      if (fieldErrors && Object.keys(fieldErrors).length) {
        setCreateErrors((prev) => ({ ...prev, ...fieldErrors }));
      }
      snackbar.show(<Text>{message}</Text>, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleFinish = async () => {
    if (!selected?.id) return;

    setFinishErrors({}); setFinishApiError('');
    try {
      await concludeAdjustmentSchema.validate(
        { description: actionDesc },
        { abortEarly: false }
      );
    } catch (yerr) {
      setFinishErrors(parseYupErrors(yerr));
      return;
    }

    setResolvingId(selected.id);
    try {
      await svc.finishAdjustment(travelId, selected.id, { description: actionDesc });
      setActionDesc('');
      await fetchAll();
    } catch (err) {
      const { message, fieldErrors } = parseApiError(err, 'Falha ao concluir pendência.');
      if (fieldErrors && Object.keys(fieldErrors).length) {
        setFinishErrors((prev) => ({ ...prev, ...fieldErrors }));
      }
      snackbar.show(<Text>{message}</Text>, { type: 'error' });
    } finally {
      setResolvingId(null);
    }
  };

  const handleCancel = async () => {
    if (!selected?.id) return;

    setAnnulErrors({}); setAnnulApiError('');
    try {
      await annulAdjustmentSchema.validate(
        { description: actionDesc },
        { abortEarly: false }
      );
    } catch (yerr) {
      setAnnulErrors(parseYupErrors(yerr));
      return;
    }

    setCancelingId(selected.id);
    try {
      await svc.cancelAdjustment(travelId, selected.id, { description: actionDesc });
      setActionDesc('');
      await fetchAll();
    } catch (err) {
      const { message, fieldErrors } = parseApiError(err, 'Falha ao anular pendência.');
      if (fieldErrors && Object.keys(fieldErrors).length) {
        setAnnulErrors((prev) => ({ ...prev, ...fieldErrors }));
      }
      snackbar.show(<Text>{message}</Text>, { type: 'error' });
    } finally {
      setCancelingId(null);
    }
  };

  const titleByMode = {
    list: 'Listagem de Pendências',
    create: 'Inclusão de pendência',
    finish: 'Conclusão de pendência',
    cancel: 'Anulação de pendência',
  };

  return (
    <BsModal
      show={isOpen}
      size="xl"
      centered
      scrollable
      onHide={handleClose}
      keyboard
      backdrop
    >
      <BsModal.Header className="px-3 py-3" closeButton>
        <Text type="header" color="dark">
          {`${titleByMode[mode]} : ${travelId}`}
        </Text>
      </BsModal.Header>

      <div className="px-3">
        {mode === 'list' && (
          <div
            className="d-flex justify-content-end align-items-center"
            style={{ paddingTop: 10, paddingBottom: 8 }}
          >
            <Button
              variant="primary"
              className="d-flex align-items-center"
              disabled={disabledCreateAdjustment}
              title={disabledCreateAdjustment}
              onClick={() => {
                if (disabledCreateAdjustment) return;
                setMode('create');
                setCreateErrors({});
                setCreateApiError('');
              }}
              aria-disabled={disabledCreateAdjustment}
            >
              <FaPlus size={12} className="mr-1" />
              <Text color="white" type="regular" weight="500">
                Inserir
              </Text>
            </Button>
          </div>
        )}
      </div>

      <BsModal.Body>
        {mode === 'list' && (
          <>
            <div style={S.statsBar}>
              <StatPill label="Total de Pendências" value={totals.total} color="#0f172a" />
              <StatPill label="Pendente(s)" value={totals.pending} color="#fb8c00" />
              <StatPill label="Anulado(s)" value={totals.total - (totals.pending + totals.finished)} color="#c62828" />
              <StatPill label="Concluído(s)" value={totals.finished} color="#2e7d32" />
            </div>

            <AdjustmentList
              loading={loading}
              items={items}
              expandedId={expandedId}
              resolvingId={resolvingId}
              cancelingId={cancelingId}
              StatusChip={StatusChip}
              fmtDate={fmtDate}
              fmtDay={fmtDay}
              elapsed={elapsed}
              userGroupSlugs={userGroupSlugs}
              currentUserId={currentUserId}
              currentUsername={currentUsername}
              onRowClick={(item) => {
                setExpandedId((prev) => (prev === item.id ? null : item.id));
                setSelected(item);
              }}
              onClickFinish={(item, canFinish) => {
                if (!canFinish) {
                  return;
                }
                setSelected(item);
                setActionDesc('');
                setFinishErrors({}); setFinishApiError('');
                setMode('finish');
              }}
              onClickCancel={(item) => {
                setSelected(item);
                setActionDesc('');
                setAnnulErrors({}); setAnnulApiError('');
                setMode('cancel');
              }}
            />
          </>
        )}

        {mode === 'create' && (
          <CreateAdjustment
            reasonLoading={reasonLoading}
            groups={groups}
            originGroups={originGroups}
            reasons={reasons}
            form={createForm}
            setForm={setCreateForm}
            onSelectGroup={handleSelectGroup}
            onSave={handleCreate}
            onCancel={() => {
              setCreateForm({ fromGroupId: '', groupSlug: '', reasonId: '', description: '' });
              setCreateErrors({}); setCreateApiError('');
              setMode('list');
            }}
            saving={saving}
            travelStatus={travelStatus}
            endLoadingOrder={endLoadingOrder}
            operationSlug="OPERATION"
            errors={createErrors}
            submitError={createApiError}
            onFieldChange={(field) => {
              setCreateErrors((e) => ({ ...e, [field]: undefined }));
              if (field === 'groupSlug') {
                setCreateErrors((e) => ({ ...e, reasonId: undefined }));
              }
            }}
          />
        )}

        {mode === 'finish' && selected && (
          <ConcludeAdjustment
            item={selected}
            elapsedText={elapsed(selected.openedAt, selected.finishedAt)}
            value={actionDesc}
            onChange={handleChangeActionDesc}
            loading={!!resolvingId}
            hideActions
            errors={finishErrors}
            submitError={finishApiError}
          />
        )}

        {mode === 'cancel' && selected && (
          <AnnulAdjustment
            item={selected}
            elapsedText={elapsed(selected.openedAt, selected.finishedAt)}
            value={actionDesc}
            onChange={handleChangeActionDesc}
            loading={!!cancelingId}
            hideActions
            errors={annulErrors}
            submitError={annulApiError}
          />
        )}
      </BsModal.Body>

      <BsModal.Footer className="d-flex justify-content-end">
        {mode === 'create' && (
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setCreateForm({ fromGroupId: '', groupSlug: '', reasonId: '', description: '' });
                setCreateErrors({}); setCreateApiError('');
                setMode('list');
              }}
              className="mr-2"
            >
              <Text type="regular" weight={500}>CANCELAR</Text>
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={!canSaveCreate}
              loading={saving}
            >
              <Text type="regular" weight={500} color="white">SALVAR</Text>
            </Button>
          </>
        )}

        {mode === 'finish' && selected && (
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setActionDesc('');
                setFinishErrors({}); setFinishApiError('');
                setMode('list');
              }}
              className="mr-2"
              disabled={!!resolvingId}
            >
              <Text type="regular" weight={500}>CANCELAR</Text>
            </Button>
            <Button
              variant="primary"
              onClick={handleFinish}
              loading={!!resolvingId}
            >
              <Text type="regular" weight={500} color="white">CONCLUIR</Text>
            </Button>
          </>
        )}

        {mode === 'cancel' && selected && (
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setActionDesc('');
                setAnnulErrors({}); setAnnulApiError('');
                setMode('list');
              }}
              className="mr-2"
              disabled={!!cancelingId}
            >
              <Text type="regular" weight={500}>CANCELAR</Text>
            </Button>
            <Button
              variant="primary"
              onClick={handleCancel}
              loading={!!cancelingId}
            >
              <Text type="regular" weight={500} color="white">ANULAR</Text>
            </Button>
          </>
        )}
      </BsModal.Footer>
    </BsModal>
  );
}
